import { defineNuxtPlugin } from "#app";
import { useRouter } from "#imports";
import { COEP_RELAXED_PATHS } from "~~/package/config/crossOriginIsolation";

// COEP/COOP(cross-origin isolation) 헤더는 HTTP 문서 로드 시점에 한 번 적용되면 그 문서가
// 사는 동안 변경 불가능하다. 이 앱은 정적 빌드 후 S3/CloudFront로 서빙되며 hydration 이후
// SPA로 라우팅하므로, SPA 이동은 문서를 새로 받지 않아 최초 로드 경로의 isolation 상태가
// 그대로 고정된다.
//
// 일부 경로(COEP_RELAXED_PATHS)는 서드파티 임베드를 위해 `COEP: unsafe-none`(non-isolated)로
// 서빙되고, 나머지는 `COEP: credentialless`(isolated)로 서빙된다. isolated 문서에서 SPA로
// non-isolated 페이지에 진입하면(또는 그 반대) 문서 헤더가 목적지와 어긋나 임베드가 깨진다.
//
// 따라서 격리 경계를 넘는 SPA 이동만 full page load로 전환해 CloudFront(live) / Nitro
// routeRules(dev,stage)가 목적지 경로에 맞는 헤더를 다시 내려주게 만든다. 판단은 순수하게
// "최초 로드 경로" 기반이라 결정적이며 reload 루프가 발생하지 않는다.
export default defineNuxtPlugin(() => {
  const router = useRouter();
  const relaxed = new Set<string>(COEP_RELAXED_PATHS);

  const stripTrailingSlash = (path: string) =>
    path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;

  // no_prefix i18n → 경로에 로케일 segment 없음 → literal 매칭 안전.
  const needsRelaxedCoep = (path: string) => relaxed.has(stripTrailingSlash(path));

  // 현재 "로드된 문서"의 isolation 모드는 부팅 시점 경로(CloudFront가 헤더를 내려준 경로)로
  // 결정된다. full reload마다 이 플러그인이 다시 실행되어 재계산되므로 항상 실제 문서 상태와
  // 일치한다.
  const loadedDocumentNeedsRelaxed = needsRelaxedCoep(window.location.pathname);

  router.beforeEach((to) => {
    // 같은 모드 → SPA 이동 그대로 진행
    if (needsRelaxedCoep(to.path) === loadedDocumentNeedsRelaxed) return;

    // isolated <-> non-isolated 경계를 넘는 이동. SPA 네비게이션은 문서의 COEP/COOP를 바꿀
    // 수 없으므로 full browser load로 전환한다 → 서버가 `to` 경로에 맞는 헤더를 내려준다.
    window.location.assign(to.fullPath); // query/hash 보존
    return false; // in-app 네비게이션 취소; hard load가 인계
  });
});
